import React, { useContext, useState } from "react";
import update from "immutability-helper";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableBodyRow from "../../components/DragAndDrop";
import CustomTable from "../../components/CustomTable";
import BeltAdd from "./BeltAdd";
import useApi from "../../hooks/useApi";
import { ListBelt, ListClass } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import BeltUpdate from "./BeltUpdate";
import usePermissions from "../../hooks/usePermissions";
import { permissionKeys } from "../../constants/staffRole";
import { AppStateContext } from "../../context/AppContext";
import { StatusChangeBelt, deleteBelt } from "../../constants/API/API";
import { message, Select, Space, Button } from "antd";
import moment from "moment";
import { Switch } from "antd";
import { useEffect } from "react";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import BeltView from "./BeltView";
import DeleteRecord from "../../components/DeleteRecord";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import { useParams } from "react-router-dom";

const Belt = () => {
  const { isAdd, isDelete, isEdit, isView } = usePermissions({
    key: permissionKeys.Employees,
    redirectPermissions: ["isView"],
  });

  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Belt Management");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedBelt, setSelectedBelt] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [ClassList, setClassList] = useState([]);
  const { request } = useRequest();
  const { id: branchId } = useParams();
  const [filters, setFilters] = useFilter({
    sortField: "createdAt",
    sort: "desc",
    startDate: null,
    endDate: null,
    branchId: undefined,
    trainerId: undefined,
    roleType: undefined,
  });
  const [viewVisible, setViewVisible] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [classRes] = await Promise.all([ListClass()]);
        setClassList(classRes.data.list || []);
      } catch (e) {
        message.error("Failed to fetch filter data");
        setClassList([]);
      }
    }
    fetchData();
  }, []);

  const addClass = () => {
    setAddModal(true);
  };

  const handleStatusChange = async (checked, belts) => {
    setLoading(true);
    try {
      await StatusChangeBelt(belts._id, { isActive: checked });
      message.success(
        `Belt ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedBelt(record);
    setEditModal(true);
  };

  const handleView = (record) => {
    setSelectedBelt(record);
    setViewVisible(true);
  };

  useApi({
    callback: ListBelt,
    payload: { ...filters, branchId },
    dependencies: [refresh, filters, branchId],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
    },
    {
      title: "Stripe Count",
      dataIndex: "stripeCount",
      key: "stripeCount",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, item) => (
        <>
          <p>{item?.name}</p>
        </>
      ),
    },
    {
      title: "Branch Name",
      dataIndex: "branchName",
      key: "branchName",
      render: (_, item) => (
        <>
          <p>{item?.branchName}</p>
        </>
      ),
    },
    {
      title: "Color Code",
      dataIndex: "colorCode",
      key: "colorCode",
      render: (_, item) => {
        const colors = Array.isArray(item?.colorCode) ? item.colorCode : (item?.colorCode ? [item.colorCode] : []);
        return colors.length > 0 ? (
          <div style={{ display: "flex", gap: "4px" }}>
            {colors.map((color, index) => (
              <span
                key={index}
                style={{
                  display: "inline-block",
                  width: 24,
                  height: 24,
                  background: color,
                  border: "1px solid #ccc",
                }}
              />
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Stripe Color Code",
      dataIndex: "stripeColor",
      key: "stripeColor",
      render: (_, item) =>
        item?.stripeColor ? (
          <span
            style={{
              display: "inline-block",
              width: 24,
              height: 24,
              background: item.stripeColor,
              border: "1px solid #ccc",
            }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
          disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, record) => moment(record.createdAt).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => handleView(record)}
            icon={<EyeOutlined />}
            title="View"
          />
          <Button
            type="primary"
            onClick={() => handleEdit(record)}
            icon={<EditOutlined />}
            title="Edit"
          />
          <DeleteRecord
            _id={record._id}
            callback={() => deleteBelt(record._id, { isDeleted: true })}
            setLoading={setLoading}
            setRefresh={setRefresh}
            trigger={true}
            type="Belt"
          />
        </Space>
      ),
    },
  ];

  const renderFilters = () => {
    return (
      <Space>
        {/* <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Class"
          onChange={handleClassFilter}
          value={filters.classId}
        >
          <Select.Option value="">All Classes</Select.Option>
          {ClassList.map((cls) => (
            <Select.Option key={cls._id} value={cls._id}>
              {cls.name}
            </Select.Option>
          ))}
        </Select> */}
      </Space>
    );
  };

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRow = data[dragIndex];
    const newData = update(data, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRow],
      ],
    });
    setData(newData);

    try {
      const orderedIds = newData.map((item) => item._id);
      request({
        url: "admin/belt/reorder",
        method: "PATCH",
        data: { orderedIds },
        onSuccess: (data) => {
          // ShowToast(data.message, Severty.SUCCESS);
        },
        onError: (err) => {
          // ShowToast(err?.response?.data?.message, Severty.ERROR);
        }
      })
    } catch (error) {
      ShowToast(error?.message, Severty.ERROR);
    }
  };

  return (
    <div>
      <Button
        type="default"
        className="mb-2"
        style={{ color: "black" }}
        onClick={() => window.history.back()}
      >
        Back
      </Button>
      <DndProvider backend={HTML5Backend}>
        <CustomTable
          addFn={addClass}
          data={data}
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          columns={columns}
          totalDocs={totalDocs}
          searchField={["name"]}
          searchPlaceholder="Search by Belt Name"
          customFilter={renderFilters}
          components={{
            body: {
              row: (props) => <DraggableBodyRow {...props} moveRow={moveRow} />,
            },
          }}
          tableProps={{
            onRow: (record, index) => ({
              index,
              moveRow,
            }),
          }}
        />
      </DndProvider>
      {addModal && isAdd && (
        <BeltAdd
          visible={addModal}
          setVisible={setAddModal}
          refresh={setRefresh}
          branchId={branchId}
        />
      )}
      {editModal && isAdd && selectedBelt && (
        <BeltUpdate
          editVisible={editModal}
          setEditVisible={setEditModal}
          selectedBelt={selectedBelt}
          refresh={setRefresh}
        />
      )}
      <BeltView
        viewVisible={viewVisible}
        setViewVisible={(visible) => {
          setViewVisible(visible);
          if (!visible) {
            setSelectedBelt(null);
          }
        }}
        selectedBelt={selectedBelt}
      />
    </div>
  );
};

export default Belt;
